import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Loader2 } from "lucide-react";

interface SettingsPageProps {
  nursingHome: NursingHome;
}

interface NotificationPreferences {
  email_notifications: boolean;
  lead_notifications: boolean;
  review_notifications: boolean;
  marketing_emails: boolean;
}

const defaultPrefs: NotificationPreferences = {
  email_notifications: true,
  lead_notifications: true,
  review_notifications: true,
  marketing_emails: false,
};

export default function SettingsPage() {
  const { nursingHome } = useOutletContext<SettingsPageProps>();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  
  // Notification settings
  const [prefs, setPrefs] = useState<NotificationPreferences>(defaultPrefs);
  
  // Contact settings
  const [contactEmail, setContactEmail] = useState(nursingHome?.email || "");
  const [contactPhone, setContactPhone] = useState(nursingHome?.standardized_phone_number || "");

  // Load notification preferences from profile
  useEffect(() => {
    const loadPrefs = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from('profiles')
        .select('notification_preferences')
        .eq('id', user.id)
        .single();
      
      if (!error && data?.notification_preferences) {
        setPrefs({ ...defaultPrefs, ...(data.notification_preferences as unknown as NotificationPreferences) });
      }
      setPrefsLoaded(true);
    };
    loadPrefs();
  }, [user]);

  const handleSaveNotifications = async () => {
    if (!user) return;
    setNotifLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ notification_preferences: prefs as any })
        .eq('id', user.id);

      if (error) throw error;
      toast.success("Notification preferences saved");
    } catch (error) {
      console.error(error);
      toast.error("Failed to save notification preferences");
    } finally {
      setNotifLoading(false);
    }
  };

  const handleSaveContact = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('nursing_homes')
        .update({
          email: contactEmail,
          standardized_phone_number: contactPhone,
        })
        .eq('nh_slug', nursingHome.nh_slug);

      if (error) throw error;
      toast.success("Contact information updated");
    } catch (error) {
      toast.error("Failed to update contact information");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">
          Manage your nursing home profile settings and preferences
        </p>
      </div>

      {/* Account Status */}
      <Card>
        <CardHeader>
          <CardTitle>Account Status</CardTitle>
          <CardDescription>Your current account status and subscription information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Profile Status</Label>
              <p className="text-sm text-muted-foreground">Your nursing home profile is verified</p>
            </div>
            <Badge variant="default" className="bg-green-100 text-green-800">Verified</Badge>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Subscription Plan</Label>
              <p className="text-sm text-muted-foreground">Basic plan with standard features</p>
            </div>
            <Badge variant="outline">Basic</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription>Update your nursing home's contact details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact-email">Email Address</Label>
              <Input
                id="contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="contact@nursinghome.ie"
              />
            </div>
            <div>
              <Label htmlFor="contact-phone">Phone Number</Label>
              <Input
                id="contact-phone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+353 1 234 5678"
              />
            </div>
          </div>
          <Button onClick={handleSaveContact} disabled={loading}>
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : "Save Contact Information"}
          </Button>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>Choose what notifications you'd like to receive</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive general email notifications</p>
            </div>
            <Switch
              checked={prefs.email_notifications}
              onCheckedChange={(v) => setPrefs(p => ({ ...p, email_notifications: v }))}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label>New Lead Alerts</Label>
              <p className="text-sm text-muted-foreground">Get notified when you receive new leads</p>
            </div>
            <Switch
              checked={prefs.lead_notifications}
              onCheckedChange={(v) => setPrefs(p => ({ ...p, lead_notifications: v }))}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label>Review Notifications</Label>
              <p className="text-sm text-muted-foreground">Get notified about new reviews</p>
            </div>
            <Switch
              checked={prefs.review_notifications}
              onCheckedChange={(v) => setPrefs(p => ({ ...p, review_notifications: v }))}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label>Marketing Emails</Label>
              <p className="text-sm text-muted-foreground">Receive updates and promotional content</p>
            </div>
            <Switch
              checked={prefs.marketing_emails}
              onCheckedChange={(v) => setPrefs(p => ({ ...p, marketing_emails: v }))}
            />
          </div>
          <Button onClick={handleSaveNotifications} disabled={notifLoading}>
            {notifLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : "Save Notification Preferences"}
          </Button>
        </CardContent>
      </Card>

      {/* Data Management */}
      <Card>
        <CardHeader>
          <CardTitle>Data Management</CardTitle>
          <CardDescription>Manage your data and account preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Export Data</Label>
              <p className="text-sm text-muted-foreground">Download a copy of your nursing home data</p>
            </div>
            <Button variant="outline">Export Data</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
